/**
 * Phase4a — Payable Summary Card (Read-Only UI Sandbox)
 *
 * Sole data source: Edge Function `admin-get-order-payable-summary`
 * This component NEVER performs its own payable calculations.
 * All figures come exclusively from the backend response.
 *
 * Contract: POST /functions/v1/admin-get-order-payable-summary
 * Body: { "order_id": "<uuid>" }
 * Response fields: summary.base_rental_total, summary.active_fee_adjustments_total,
 *                  summary.active_discount_adjustments_total, summary.final_payable_total,
 *                  adjustments[]
 *
 * Uses supabase.functions.invoke() to auto-include apikey + Authorization headers.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../../services/supabaseClient';

interface Adjustment {
  id: string;
  order_id: string;
  adjustment_type: string;
  amount: number;
  reason: string;
  is_active: boolean;
  voided_at?: string | null;
}

interface PayableSummaryData {
  base_rental_total: number;
  active_fee_adjustments_total: number;
  active_discount_adjustments_total: number;
  final_payable_total: number;
  adjustments: Adjustment[];
  currency: string;
  computed_at: string;
}

interface PayableSummaryCardProps {
  orderId: string;
}

type LoadState = 'idle' | 'loading' | 'success' | 'unavailable' | 'error';

function formatNT(amount: number, currency = 'TWD'): string {
  const prefix = currency === 'TWD' ? 'NT$\u00a0' : `${currency}\u00a0`;
  return `${prefix}${Math.abs(amount).toLocaleString()}`;
}

export default function PayableSummaryCard({ orderId }: PayableSummaryCardProps) {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [data, setData] = useState<PayableSummaryData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoadState('loading');
    setErrorMsg(null);
    try {
      // Use supabase.functions.invoke to auto-include apikey + Authorization headers
      const { data: responseData, error: invokeError } = await supabase.functions.invoke(
        'admin-get-order-payable-summary',
        { body: { order_id: orderId } },
      );

      if (invokeError) {
        const msg = String(invokeError?.message || invokeError);
        // 404 / not found → EF not deployed yet
        if (msg.includes('404') || msg.includes('not found') || msg.includes('501')) {
          setLoadState('unavailable');
          return;
        }
        // Network errors → unavailable
        if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
          setLoadState('unavailable');
          return;
        }
        setErrorMsg(msg);
        setLoadState('error');
        return;
      }

      const summary = responseData?.summary;
      if (
        summary &&
        typeof summary.base_rental_total === 'number' &&
        typeof summary.final_payable_total === 'number'
      ) {
        setData({
          base_rental_total: summary.base_rental_total,
          active_fee_adjustments_total: summary.active_fee_adjustments_total ?? 0,
          active_discount_adjustments_total: summary.active_discount_adjustments_total ?? 0,
          final_payable_total: summary.final_payable_total,
          adjustments: responseData?.adjustments ?? [],
          currency: summary.currency || 'TWD',
          computed_at: summary.computed_at || new Date().toISOString(),
        });
        setLoadState('success');
      } else {
        // Response OK but no summary → EF returned empty / sandbox response
        setLoadState('unavailable');
      }
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setLoadState('unavailable');
      } else {
        setErrorMsg(msg);
        setLoadState('error');
      }
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      fetchSummary();
    }
  }, [orderId, fetchSummary]);

  // ── Loading ──
  if (loadState === 'loading' || loadState === 'idle') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/2 mb-4"></div>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex justify-between mb-3">
            <div className="h-3 bg-gray-100 rounded w-2/5"></div>
            <div className="h-3 bg-gray-100 rounded w-1/5"></div>
          </div>
        ))}
      </div>
    );
  }

  // ── EF Unavailable (Sandbox) ──
  if (loadState === 'unavailable') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-money-dollar-circle-line text-amber-500"></i>
          Payable Summary
          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-normal">
            Sandbox
          </span>
        </h2>
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-700 leading-relaxed">
          <i className="ri-information-line mr-1"></i>
          Edge Function <code className="bg-amber-100 px-1 rounded">admin-get-order-payable-summary</code> 尚未部署。
          此卡片為 UI Sandbox，待 EF 上線後自動啟用。
        </div>
        <div className="mt-4 space-y-2 opacity-40 select-none">
          {['Base Rental Total', 'Active Fee Adjustments', 'Active Discount Adjustments', 'Final Payable Total'].map((label, i) => (
            <div key={i} className={`flex justify-between text-sm py-1 ${i === 3 ? 'border-t border-gray-200 pt-3 font-semibold' : ''}`}>
              <span className="text-gray-500">{label}</span>
              <span className="text-gray-400">—</span>
            </div>
          ))}
        </div>
        <button
          onClick={fetchSummary}
          className="mt-4 w-full text-xs text-teal-600 hover:text-teal-800 cursor-pointer py-1"
        >
          <i className="ri-refresh-line mr-1"></i>
          重新嘗試
        </button>
      </div>
    );
  }

  // ── Error ──
  if (loadState === 'error') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-money-dollar-circle-line text-red-500"></i>
          Payable Summary
        </h2>
        <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-xs text-red-700">
          <i className="ri-error-warning-line mr-1"></i>
          {errorMsg || '載入失敗'}
        </div>
        <button
          onClick={fetchSummary}
          className="mt-3 w-full text-xs text-teal-600 hover:text-teal-800 cursor-pointer py-1"
        >
          <i className="ri-refresh-line mr-1"></i>
          重試
        </button>
      </div>
    );
  }

  // ── Success ──
  if (!data) return null;

  const currency = data.currency || 'TWD';
  const discountAmt = data.active_discount_adjustments_total;
  const feeAmt = data.active_fee_adjustments_total;
  const activeAdjustments = data.adjustments?.filter((a) => a.is_active) ?? [];
  const voidedAdjustments = data.adjustments?.filter((a) => !a.is_active && a.voided_at) ?? [];

  return (
    <div className="bg-gray-900 rounded-lg shadow-sm border border-gray-700 p-6">
      <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
        <i className="ri-money-dollar-circle-line text-amber-400"></i>
        Payable Summary
        <span className="text-xs text-gray-400 ml-auto font-normal">
          唯一資料來源：EF
        </span>
      </h2>
      <div className="space-y-3">
        {/* Base Rental Total */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Base Rental Total</span>
          <span className="font-medium text-gray-100">{formatNT(data.base_rental_total, currency)}</span>
        </div>

        {/* Active Fee Adjustments */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Active Fee Adjustments</span>
          <span className={`font-medium ${feeAmt >= 0 ? 'text-amber-300' : 'text-red-300'}`}>
            {feeAmt >= 0 ? '+' : '−'} {formatNT(feeAmt, currency)}
          </span>
        </div>

        {/* Active Discount Adjustments */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Active Discount Adjustments</span>
          <span className="font-medium text-emerald-400">
            {discountAmt !== 0 ? `−\u00a0${formatNT(Math.abs(discountAmt), currency)}` : formatNT(0, currency)}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 pt-3 mt-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-white">Final Payable Total</span>
            <span className="text-xl font-bold text-amber-400">{formatNT(data.final_payable_total, currency)}</span>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            <i className="ri-information-line mr-1"></i>
            此金額僅作參考，以合約為準
          </div>
          {data.computed_at && (
            <div className="text-xs text-gray-600 mt-1">
              計算時間：{new Date(data.computed_at).toLocaleString('zh-TW')}
            </div>
          )}
        </div>
      </div>

      {/* Active Adjustments List */}
      {activeAdjustments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">生效中的調整</h3>
          <div className="space-y-2">
            {activeAdjustments.map((adj) => (
              <div key={adj.id} className="flex items-center justify-between text-xs">
                <span className="text-gray-400 truncate max-w-[60%]">{adj.reason || adj.adjustment_type}</span>
                <span className={`font-medium ${adj.amount >= 0 ? 'text-amber-300' : 'text-emerald-400'}`}>
                  {adj.amount >= 0 ? '+' : '−'} {formatNT(Math.abs(adj.amount), currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voided Adjustments List (audit-only) */}
      {voidedAdjustments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">已作廢的調整（僅供稽核）</h3>
          <div className="space-y-2">
            {voidedAdjustments.map((adj) => (
              <div key={adj.id} className="flex items-center justify-between text-xs opacity-60">
                <span className="text-gray-500 truncate max-w-[60%]">{adj.reason || adj.adjustment_type}</span>
                <span className="font-medium text-gray-500">
                  {adj.amount >= 0 ? '+' : '−'} {formatNT(Math.abs(adj.amount), currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={fetchSummary}
        className="mt-4 w-full text-xs text-gray-400 hover:text-gray-200 cursor-pointer py-1 transition-colors"
      >
        <i className="ri-refresh-line mr-1"></i>
        刷新
      </button>
    </div>
  );
}